import type { FormEvent } from 'react'
import { useState } from 'react'
import { Button, Surface, TextField } from '@/components/ui'
import { Logo } from '@/components/layout'
import styles from './AdminLogin.module.css'

interface AdminLoginProps {
  onSignIn: (email: string, password: string) => Promise<void>
}

/** Email and password, exchanged for a bearer token by the API. */
export const AdminLogin = ({ onSignIn }: AdminLoginProps) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setIsBusy(true)
    try {
      await onSignIn(email, password)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div className={styles.wrap}>
      <Surface pad={30} radius="card" className={styles.card}>
        <div className={styles.head}>
          <Logo size={30} />
          <p className={`t-14 ${styles.sub}`}>Sign in to edit the site.</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <TextField
            label="Email"
            name="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button type="submit" variant="solid" fullWidth disabled={isBusy}>
            {isBusy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Surface>
    </div>
  )
}
